import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SLOTS = [
  { key: "cover",    label: "Cover · Living Area" },
  { key: "kitchen",  label: "Kitchen" },
  { key: "bedroom",  label: "Master Bedroom" },
  { key: "bathroom", label: "Bathroom" },
];

/**
 * DesignGalleryModal — admin Design Solution photo manager.
 *
 * Each solution has 4 independent photo slots. Regenerating one slot NEVER
 * touches the others — Shannon can keep the 3 she loves + regen the one she
 * hates. Custom uploads bypass AI entirely.
 *
 * Every slot keeps up to 5 previous versions in history so a botched regen
 * can be reverted with one tap.
 */
export default function DesignGalleryModal({ open, onClose }) {
  const [solutions, setSolutions] = useState([]);
  const [activeSolutionId, setActiveSolutionId] = useState("");
  const [gallery, setGallery] = useState(null);
  const [busySlot, setBusySlot] = useState("");
  const [busyKind, setBusyKind] = useState("");   // 'regen' | 'upload' | 'revert'
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/design-solutions`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        setSolutions(d.solutions || []);
        setActiveSolutionId(prev => prev || (d.solutions?.[0]?.id || ""));
      } catch { /* silent */ }
    })();
  }, [open]);

  useEffect(() => {
    if (!activeSolutionId) { setGallery(null); return; }
    loadGallery(activeSolutionId);
  }, [activeSolutionId]);

  const loadGallery = async (solutionId) => {
    setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/design-solutions/${solutionId}/gallery`, { credentials: "include" });
      if (!r.ok) { setError("Could not load gallery."); return; }
      const d = await r.json();
      setGallery(d.gallery);
    } catch { setError("Network error."); }
  };

  const regenerate = async (slot) => {
    setBusySlot(slot); setBusyKind("regen"); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/design-solutions/${activeSolutionId}/gallery/regenerate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slot }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.detail || "Regeneration failed.");
      else setGallery(d.gallery);
    } catch { setError("Network error."); }
    setBusySlot(""); setBusyKind("");
  };

  const upload = async (slot, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setError("Max 8 MB per image."); return; }
    setBusySlot(slot); setBusyKind("upload"); setError("");
    try {
      const b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = reject;
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(file);
      });
      const r = await fetch(`${BACKEND_URL}/api/admin/design-solutions/${activeSolutionId}/gallery/upload`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slot, image_base64: String(b64) }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.detail || "Upload failed.");
      else setGallery(d.gallery);
    } catch { setError("Network error."); }
    setBusySlot(""); setBusyKind("");
  };

  const revert = async (slot, versionId) => {
    setBusySlot(slot); setBusyKind("revert"); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/design-solutions/${activeSolutionId}/gallery/revert`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slot, version_id: versionId }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.detail || "Revert failed.");
      else setGallery(d.gallery);
    } catch { setError("Network error."); }
    setBusySlot(""); setBusyKind("");
  };

  if (!open) return null;

  const activeSolution = solutions.find(s => s.id === activeSolutionId);

  return (
    <div
      data-testid="design-gallery-backdrop"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.78)", backdropFilter: "blur(6px)", zIndex: 2500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="design-gallery-modal"
        style={{ background: "linear-gradient(180deg, #0F1729 0%, #0B1220 100%)", border: "1px solid rgba(90,200,216,0.35)", borderRadius: 14, padding: "1.5rem 1.6rem", maxWidth: 900, width: "100%", maxHeight: "92vh", overflowY: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <p style={{ color: "#5AC8D8", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", margin: 0 }}>DESIGN STUDIO · GALLERY MANAGER</p>
            <h2 style={{ color: "#F3F4F6", fontSize: "1.35rem", fontWeight: 800, margin: "4px 0 6px" }}>🎨 Solution Photos</h2>
            <p style={{ color: "#7E92A8", fontSize: "0.82rem", margin: 0, maxWidth: 600 }}>
              Each Solution has 4 independent photo slots. Regenerate the ones you don&apos;t love — the others stay locked. Upload your own to bypass AI entirely.
            </p>
          </div>
          <button
            data-testid="design-gallery-close"
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}
          >×</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Design Solution</label>
          <select
            data-testid="design-gallery-solution-picker"
            value={activeSolutionId}
            onChange={(e) => setActiveSolutionId(e.target.value)}
            style={inputStyle}
          >
            {solutions.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {activeSolution?.tagline && (
            <p style={{ color: "#7E92A8", fontSize: "0.78rem", margin: "6px 0 0", fontStyle: "italic" }}>{activeSolution.tagline}</p>
          )}
        </div>

        {error && (
          <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", color: "#FCA5A5", padding: "0.6rem 0.8rem", borderRadius: 8, fontSize: "0.82rem", marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
          {SLOTS.map(s => {
            const current = gallery?.slots?.[s.key];
            const history = gallery?.history?.[s.key] || [];
            const isBusy = busySlot === s.key;
            return (
              <div key={s.key} data-testid={`gallery-slot-${s.key}`} style={{ background: "rgba(15,20,30,0.6)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "0.85rem 0.95rem" }}>
                <p style={{ color: "#5AC8D8", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.06em", margin: "0 0 8px" }}>{s.label.toUpperCase()}</p>

                <div style={{ height: 160, borderRadius: 10, overflow: "hidden", background: "rgba(6,12,20,0.5)", border: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                  {current?.url ? (
                    <img
                      src={`${BACKEND_URL}${current.url}`}
                      alt={s.label}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span style={{ color: "#7E92A8", fontSize: "0.75rem", letterSpacing: "0.06em", fontWeight: 700 }}>NO PHOTO YET</span>
                  )}
                </div>

                {current && (
                  <p style={{ color: "#7E92A8", fontSize: "0.7rem", margin: "0 0 8px" }}>
                    {current.source === "ai" ? "🤖 Platform-generated" : "📤 Uploaded"} · {new Date(current.created_at).toLocaleDateString()}
                  </p>
                )}

                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    data-testid={`gallery-slot-${s.key}-regen`}
                    onClick={() => regenerate(s.key)}
                    disabled={isBusy}
                    style={{ ...btnPrimary, flex: 1, opacity: isBusy ? 0.5 : 1, cursor: isBusy ? "wait" : "pointer" }}
                  >
                    {isBusy && busyKind === "regen" ? "…" : "↻ Regen"}
                  </button>
                  <label
                    data-testid={`gallery-slot-${s.key}-upload-label`}
                    style={{ ...btnSecondary, flex: 1, opacity: isBusy ? 0.5 : 1, cursor: isBusy ? "wait" : "pointer" }}
                  >
                    {isBusy && busyKind === "upload" ? "…" : "📤 Upload"}
                    <input
                      data-testid={`gallery-slot-${s.key}-upload-input`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => upload(s.key, e.target.files?.[0])}
                      disabled={isBusy}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>

                {history.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
                    <p style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, margin: "0 0 6px", letterSpacing: "0.05em" }}>PREVIOUS ({history.length}):</p>
                    <div style={{ display: "flex", gap: 6, overflowX: "auto" }}>
                      {history.map(h => (
                        <button
                          key={h.version_id}
                          data-testid={`gallery-slot-${s.key}-revert-${h.version_id}`}
                          onClick={() => revert(s.key, h.version_id)}
                          disabled={isBusy}
                          title={`Revert to ${new Date(h.created_at).toLocaleString()}`}
                          style={{ width: 40, height: 40, borderRadius: 6, overflow: "hidden", border: "1px solid rgba(255,255,255,0.15)", background: "transparent", padding: 0, cursor: "pointer", flexShrink: 0 }}
                        >
                          <img src={`${BACKEND_URL}${h.url}`} alt="prev" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 16, textAlign: "center" }}>
          AI generation uses GPT-Image-1 tuned with this Solution&apos;s palette + narrative + module hints. Each regen takes ~15 seconds.
        </p>
      </div>
    </div>
  );
}

const labelStyle = { display: "block", color: "#ADBED1", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 4, textTransform: "uppercase" };

const inputStyle = {
  width: "100%",
  background: "rgba(15,20,30,0.6)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8,
  padding: "0.7rem 0.85rem",
  color: "#F3F4F6",
  fontSize: "0.92rem",
  outline: "none",
};

const btnPrimary = {
  background: "linear-gradient(90deg, #5AC8D8, #3AA8B8)",
  color: "#062617",
  border: "none",
  borderRadius: 8,
  padding: "0.55rem 0.7rem",
  fontWeight: 800,
  fontSize: "0.78rem",
  cursor: "pointer",
  textAlign: "center",
};

const btnSecondary = {
  background: "rgba(255,255,255,0.06)",
  color: "#E6ECF2",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8,
  padding: "0.55rem 0.7rem",
  fontWeight: 700,
  fontSize: "0.78rem",
  cursor: "pointer",
  textAlign: "center",
};
